import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Users, CheckCircle, DollarSign, Shield, Clock, ExternalLink, Link, Video } from "lucide-react";
import { EditableAtAGlance } from "@/components/admin/EditableAtAGlance";
import { EditableCategories } from "@/components/admin/EditableCategories";
import { EditableBestFor } from "@/components/admin/EditableBestFor";

interface EnhancedAtAGlanceProps {
  product: any;
  isEditor: boolean;
  isEditMode: boolean;
  onUpdateField: (field: string, value: any) => Promise<void>;
  onBookCall?: () => void;
  onWatchDemo?: () => void;
  onReadReviews?: () => void;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section = ({ icon, title, children }: SectionProps) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <div className="text-blue-500">{icon}</div>
      <h3 className="font-semibold text-sm">{title}</h3>
    </div>
    <div className="text-sm text-muted-foreground pl-7">{children}</div>
  </div>
);

export const EnhancedAtAGlance = ({
  product,
  isEditor,
  isEditMode,
  onUpdateField,
  onBookCall,
  onWatchDemo,
  onReadReviews,
}: EnhancedAtAGlanceProps) => {
  const atAGlance = product.at_a_glance;

  if (!atAGlance) {
    return isEditor && isEditMode ? (
      <div className="border-2 border-dashed rounded-lg p-8 text-center">
        <p className="text-muted-foreground mb-4">No at-a-glance data configured</p>
        <EditableAtAGlance
          value={null}
          onSave={(value) => onUpdateField('at_a_glance', value)}
        />
      </div>
    ) : null;
  }

  return (
    <>
      {/* Categories */}
      {(product.categories && product.categories.length > 0) || (isEditor && isEditMode) ? (
        <div className="mb-6">
          <EditableCategories
            value={product.categories || []}
            onSave={(value) => onUpdateField('categories', value)}
          />
        </div>
      ) : null}

      {/* Best For */}
      {(product.best_for && product.best_for.length > 0) || (isEditor && isEditMode) ? (
        <div className="mb-6">
          <EditableBestFor
            productId={product.slug || ''}
            initialValue={product.best_for || []}
          />
        </div>
      ) : null}

      <Card>
        <CardContent className="pt-6 space-y-6">
        {/* Header with tagline and website */}
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-2xl font-bold">At-a-glance</h2>
            {product.website && (
              <a 
                href={product.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:underline text-sm font-medium"
              >
                {product.website.replace(/^https?:\/\//, '')}
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-l-4 border-primary rounded-lg p-4">
            <p className="text-base md:text-lg font-semibold text-foreground">
              ⏱️ You're busy & on the go, so Circle did the homework.
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              We've analyzed everything so you can make a fast, confident decision.
            </p>
          </div>
        </div>
        
        <div className="border-t pt-6 space-y-6">
          {/* Quick Summary */}
          {atAGlance.quick_summary && (
            <Section icon={<Sparkles className="w-4 h-4" />} title="Quick Summary">
              <p>{atAGlance.quick_summary}</p>
            </Section>
          )}

          {/* Who It's For */}
          {atAGlance.who_its_for && (
            <Section icon={<Users className="w-4 h-4" />} title="Who It's For">
              <div className="space-y-1">
                {atAGlance.who_its_for.skill_level && (
                  <p><span className="font-medium">Skill level:</span> {atAGlance.who_its_for.skill_level}</p>
                )}
                {atAGlance.who_its_for.typical_profile && (
                  <p><span className="font-medium">Typical profile:</span> {atAGlance.who_its_for.typical_profile}</p>
                )}
                {atAGlance.who_its_for.other_criteria && atAGlance.who_its_for.other_criteria.length > 0 && (
                  <ul className="list-disc list-inside">
                    {atAGlance.who_its_for.other_criteria.map((criteria: string, idx: number) => (
                      <li key={idx}>{criteria}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Section>
          )}

          {/* What You Get */}
          {atAGlance.what_you_get && atAGlance.what_you_get.length > 0 && (
            <Section icon={<CheckCircle className="w-4 h-4" />} title="What You Get">
              <ul className="space-y-1">
                {atAGlance.what_you_get.map((item: any, idx: number) => {
                  // Handle both string format and object format {icon, text, type}
                  if (typeof item === 'string') {
                    return (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    );
                  }
                  // Handle object format with icon
                  return (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="shrink-0">{item.icon}</span>
                      <span>{item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {/* Speed to Value */}
          {atAGlance.speed_to_value && (
            <Section icon={<Clock className="w-4 h-4" />} title="Speed to Value">
              <div className="space-y-1">
                {atAGlance.speed_to_value.setup_time && (
                  <p><span className="font-medium">Setup time:</span> {atAGlance.speed_to_value.setup_time}</p>
                )}
                {atAGlance.speed_to_value.first_impact && (
                  <p><span className="font-medium">First impact:</span> {atAGlance.speed_to_value.first_impact}</p>
                )}
              </div>
            </Section>
          )}

          {/* Guarantee and Risk Reversal */}
          {atAGlance.guarantee && (
            <Section icon={<Shield className="w-4 h-4" />} title="Guarantee and Risk Reversal">
              <div className="space-y-1">
                {atAGlance.commitment?.cancellation_terms && (
                  <p>{atAGlance.commitment.cancellation_terms}</p>
                )}
                {atAGlance.guarantee.type && (
                  <p><span className="font-medium">{atAGlance.guarantee.type}:</span> {atAGlance.guarantee.terms}</p>
                )}
              </div>
            </Section>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3 pt-4">
            {onWatchDemo && product.demo_video_url && (
              <Button onClick={onWatchDemo} variant="default">
                Watch demo
              </Button>
            )}
            {onBookCall && product.booking_url && (
              <Button onClick={onBookCall} variant="default">
                Book a call
              </Button>
            )}
            {onReadReviews && (
              <Button onClick={onReadReviews} variant="outline">
                Read reviews
              </Button>
            )}
          </div>

          {/* Edit section for admins */}
          {isEditor && isEditMode && (
            <div className="pt-4 border-t space-y-4">
              {/* Links & CTAs */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  Links & CTAs
                </h4>
                <div className="grid gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="demo_video_url" className="text-xs flex items-center gap-1">
                      <Video className="w-3 h-3" />
                      Demo Video URL
                    </Label>
                    <Input
                      id="demo_video_url"
                      placeholder="https://..."
                      defaultValue={product.demo_video_url || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (product.demo_video_url || '')) {
                          onUpdateField('demo_video_url', e.target.value || null);
                        }
                      }}
                      className="text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="booking_url" className="text-xs flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />
                      Booking URL
                    </Label>
                    <Input
                      id="booking_url"
                      placeholder="https://calendly.com/..."
                      defaultValue={product.booking_url || ''}
                      onBlur={(e) => {
                        if (e.target.value !== (product.booking_url || '')) {
                          onUpdateField('booking_url', e.target.value || null);
                        }
                      }}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* At-a-glance editor */}
              <EditableAtAGlance
                value={atAGlance}
                onSave={(value) => onUpdateField('at_a_glance', value)}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </>
  );
};
